import React from "react";
import { PurchaseOrderDetails } from "../../../../../contract/dtos/purchaseRequest";
import { formatDateWithMoment } from "../../../../../utils/src/date";

interface OrderSummaryProps {
  purchaseOrder: PurchaseOrderDetails;
  userPermissions: string[];
  canViewPurchaseRequests: boolean;
  isSuperAdmin: boolean;
  VIEW_PURCHASE_REQUEST_PERMISSION: string;
  onPRClick: (prId: string, branchId: string) => void;
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  purchaseOrder,
  userPermissions,
  canViewPurchaseRequests,
  isSuperAdmin,
  VIEW_PURCHASE_REQUEST_PERMISSION,
  onPRClick,
}) => {
  const currencyCode =
    purchaseOrder?.purchase_requests?.[0]?.purchase_request_price?.currency?.code ??
    purchaseOrder?.items?.[0]?.price?.currency?.code ??
    "SAR";

  const formatCurrency = (value?: number) =>
    `${(value ?? 0).toFixed(1)} ${currencyCode}`;

  return (
    <div className="bg-white p-6">
      <h2 className="text-lg font-semibold mb-4">Summary</h2>

      {/* Cost Breakdown */}
      <div className="space-y-3 mb-6">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Sub total</span>
          <span>{formatCurrency(purchaseOrder?.order_summary?.subtotal)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Included discount</span>
          <span>
            {formatCurrency(purchaseOrder?.order_summary?.included_discount)}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Sub total discount</span>
          <span>
            {formatCurrency(purchaseOrder?.order_summary?.subtotal_discount)}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Shipping fees</span>
          <span>
            {formatCurrency(purchaseOrder?.order_summary?.shipping_fees)}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">
            VAT ({purchaseOrder?.order_summary?.VAT_percentage?.toFixed(1) ?? 0}%)
          </span>
          <span>
            {formatCurrency(purchaseOrder?.order_summary?.VAT ?? 0)}
          </span>
        </div>
        <div className="border-t border-gray-200 pt-3">
          <div className="flex justify-between text-sm">
            <span className="font-semibold">Total</span>
            <span>{formatCurrency(purchaseOrder?.order_summary?.total)}</span>
          </div>
        </div>
      </div>

      {/* Order Details */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-gray-700 mb-1">Branch</label>
          <div className="text-sm py-2 font-medium">
            {purchaseOrder?.branch?.name}
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">
            Additional notes
          </label>
          <div className="text-sm text-gray-900 py-2 min-h-[60px] font-medium">
            {purchaseOrder?.additional_notes ?? "No additional notes"}
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">
            PRs ({purchaseOrder?.purchase_requests?.length || 0})
          </label>
          <div className="flex flex-wrap gap-1">
            {purchaseOrder?.purchase_requests?.map((pr: any, index: any) => (
              <span key={index} className="text-sm">
                {canViewPurchaseRequests &&
                userPermissions.includes(VIEW_PURCHASE_REQUEST_PERMISSION) ? (
                  <button
                    className="underline cursor-pointer hover:text-blue-600"
                    onClick={() => onPRClick(pr.id, pr.branch?.id || "")}
                  >
                    {pr.id}
                  </button>
                ) : isSuperAdmin ? (
                  <span
                    className="underline cursor-pointer hover:text-blue-600"
                    onClick={() => onPRClick(pr.id, pr.branch?.id || "")}
                  >
                    {pr.id}
                  </span>
                ) : (
                  <button className="underline cursor-pointer !cursor-not-allowed text-gray-400">
                    {pr.id}
                  </button>
                )}
                {index < (purchaseOrder?.purchase_requests?.length || 0) - 1 &&
                  ","}
              </span>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">Ordered By</label>
          <div className="text-sm text-gray-900">
            {purchaseOrder?.ordered_by}
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">
            Date created
          </label>
          <div className="text-sm text-gray-900">
            {formatDateWithMoment(purchaseOrder?.created_at || "")}
          </div>
        </div>
      </div>
    </div>
  );
};
